import { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';
import { 
  ArrowLeft, Mail, MessageCircle, HelpCircle, 
  Star, Send, ChevronRight, Heart, Shield,
  Sparkles, BookOpen, Phone
} from 'lucide-react';
import { Button } from '../components/ui/button';
import ShubhHeader from '../components/shubh/ShubhHeader';
import ShubhBottomNav from '../components/shubh/ShubhBottomNav';

const HelpPage = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const { isDark } = useTheme();
  const [feedback, setFeedback] = useState('');
  const [feedbackSent, setFeedbackSent] = useState(false);

  const founderEmail = 'founder@nakshatra.net.in';

  const content = {
    hi: {
      title: 'सहायता और फीडबैक',
      subtitle: 'हम आपकी मदद के लिए यहां हैं',
      contactFounder: 'संस्थापक से संपर्क करें',
      founderNote: 'अपने सुझाव, प्रश्न या फीडबैक सीधे हमारे संस्थापक को भेजें',
      emailUs: 'ईमेल करें',
      faq: 'अक्सर पूछे जाने वाले प्रश्न',
      faqs: [
        {
          q: 'कुंडली कैसे बनाएं?',
          a: 'प्रोफाइल बनाएं → जन्म विवरण दर्ज करें → "जन्म कुंडली" पर जाएं → "कुंडली बनाएं" पर क्लिक करें'
        },
        {
          q: 'कुंडली मिलान कैसे करें?',
          a: '"कुंडली मिलान" पेज पर जाएं → दोनों प्रोफाइल चुनें → "मिलान देखें" पर क्लिक करें'
        },
        {
          q: 'प्रीमियम प्लान क्या है?',
          a: 'Stellar (₹99/वर्ष) में 2 प्रोफाइल और असीमित फीचर्स मिलते हैं। Cosmic (₹199/वर्ष) में असीमित प्रोफाइल मिलते हैं।'
        },
        {
          q: 'हस्तरेखा कैसे देखें?',
          a: '"हस्तरेखा" पेज पर जाएं → अपनी हथेली की फोटो अपलोड करें → AI विश्लेषण प्राप्त करें'
        }
      ],
      feedbackTitle: 'फीडबैक भेजें',
      feedbackPlaceholder: 'अपना सुझाव या प्रश्न लिखें...',
      sendFeedback: 'भेजें',
      feedbackThanks: 'धन्यवाद! आपका फीडबैक प्राप्त हुआ।',
      madeWith: 'भारत में ❤️ से बनाया गया',
      version: 'संस्करण 1.0.0'
    },
    en: {
      title: 'Help & Feedback',
      subtitle: "We're here to help you",
      contactFounder: 'Contact Founder',
      founderNote: 'Send your suggestions, questions or feedback directly to our founder',
      emailUs: 'Email Us',
      faq: 'Frequently Asked Questions',
      faqs: [
        {
          q: 'How to generate Kundli?',
          a: 'Create profile → Enter birth details → Go to "Janam Kundli" → Click "Generate Kundli"'
        },
        {
          q: 'How to do Kundli matching?',
          a: 'Go to "Kundli Milan" page → Select both profiles → Click "Check Compatibility"'
        },
        {
          q: 'What is Premium plan?',
          a: 'Stellar (₹99/year) gives 2 profiles and unlimited features. Cosmic (₹199/year) gives unlimited profiles.'
        },
        {
          q: 'How to read palm?',
          a: 'Go to "Palm Reading" page → Upload your palm photo → Get AI analysis'
        }
      ],
      feedbackTitle: 'Send Feedback',
      feedbackPlaceholder: 'Write your suggestion or question...',
      sendFeedback: 'Send',
      feedbackThanks: 'Thank you! Your feedback has been received.',
      madeWith: 'Made with ❤️ in India',
      version: 'Version 1.0.0'
    }
  };

  const t = content[language] || content.en;

  const handleSendFeedback = () => {
    if (feedback.trim()) {
      // Open email client with pre-filled content
      const subject = encodeURIComponent('Nakshatra App Feedback');
      const body = encodeURIComponent(feedback);
      window.location.href = `mailto:${founderEmail}?subject=${subject}&body=${body}`;
      setFeedbackSent(true);
      setFeedback('');
      setTimeout(() => setFeedbackSent(false), 3000);
    }
  };

  return (
    <div className="min-h-screen cosmic-bg pb-28">
      <ShubhHeader title={t.title} showBack />

      <div className="max-w-2xl mx-auto px-4 py-6 space-y-6">
        {/* Contact Founder Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className={`p-6 rounded-2xl ${isDark ? 'bg-gradient-to-br from-white to-[#FCE9D9] border border-[#F5B942]/40' : 'bg-gradient-to-br from-white to-[#FCE9D9] border border-[#F5B942]/40'}`}
        >
          <div className="flex items-start gap-4">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#C84B32] to-[#E8913A] flex items-center justify-center flex-shrink-0">
              <Mail className="w-7 h-7 text-white" />
            </div>
            <div className="flex-1">
              <h2 className={`text-lg font-semibold mb-1 ${isDark ? 'text-white' : 'text-[#282622]'}`}>
                {t.contactFounder}
              </h2>
              <p className={`text-sm mb-4 ${isDark ? 'text-[#4A4640]' : 'text-[#6B6560]'}`}>
                {t.founderNote}
              </p>
              <a
                href={`mailto:${founderEmail}`}
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-[#C84B32] to-amber-500 text-[#282622] font-semibold rounded-xl hover:from-amber-500 hover:to-[#F5B942] transition-all"
              >
                <Send className="w-4 h-4" />
                {t.emailUs}
              </a>
              <p className={`mt-3 text-sm ${isDark ? 'text-[#C84B32]' : 'text-[#C84B32]'}`}>
                {founderEmail}
              </p>
            </div>
          </div>
        </motion.div>

        {/* Quick Feedback */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className={`p-5 rounded-2xl ${isDark ? 'bg-[#FCE9D9]/40 border border-[#282622]/10' : 'bg-white border border-[#282622]/10'}`}
        >
          <h3 className={`text-lg font-semibold mb-3 flex items-center gap-2 ${isDark ? 'text-white' : 'text-[#282622]'}`}>
            <MessageCircle className="w-5 h-5 text-[#C84B32]" />
            {t.feedbackTitle}
          </h3>
          
          {feedbackSent ? (
            <div className="p-4 rounded-xl bg-green-500/20 text-green-600 text-center">
              <Sparkles className="w-6 h-6 mx-auto mb-2" />
              {t.feedbackThanks}
            </div>
          ) : (
            <>
              <textarea
                value={feedback}
                onChange={(e) => setFeedback(e.target.value)}
                placeholder={t.feedbackPlaceholder}
                rows={3}
                className={`w-full p-3 rounded-xl resize-none ${
                  isDark 
                    ? 'bg-[#FCE9D9]/40 border border-[#282622]/10 text-white placeholder-gray-500' 
                    : 'bg-[#FFF9EF] border border-[#282622]/10 text-[#282622] placeholder-gray-400'
                } focus:outline-none focus:ring-2 focus:ring-[#C84B32]/30`}
              />
              <Button
                onClick={handleSendFeedback}
                disabled={!feedback.trim()}
                className="mt-3 w-full bg-gradient-to-r from-[#C84B32] to-[#E85D43] hover:from-[#A63D28] hover:to-[#C84B32] text-white disabled:opacity-60"
              >
                <Send className="w-4 h-4 mr-2" />
                {t.sendFeedback}
              </Button>
            </>
          )}
        </motion.div>

        {/* FAQ Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className={`p-5 rounded-2xl ${isDark ? 'bg-[#FCE9D9]/40 border border-[#282622]/10' : 'bg-white border border-[#282622]/10'}`}
        >
          <h3 className={`text-lg font-semibold mb-4 flex items-center gap-2 ${isDark ? 'text-white' : 'text-[#282622]'}`}>
            <HelpCircle className="w-5 h-5 text-blue-500" />
            {t.faq}
          </h3>
          
          <div className="space-y-3">
            {t.faqs.map((faq, index) => (
              <details
                key={index}
                className={`group rounded-xl overflow-hidden ${isDark ? 'bg-[#FCE9D9]/40' : 'bg-[#FFF9EF]'}`}
              >
                <summary className={`flex items-center justify-between p-4 cursor-pointer ${isDark ? 'text-white hover:bg-[#FCE9D9]/60' : 'text-[#282622] hover:bg-[#FCE9D9]/60'}`}>
                  <span className="font-medium text-sm pr-4">{faq.q}</span>
                  <ChevronRight className="w-4 h-4 transition-transform group-open:rotate-90" />
                </summary>
                <div className={`px-4 pb-4 text-sm ${isDark ? 'text-[#6B6560]' : 'text-[#6B6560]'}`}>
                  {faq.a}
                </div>
              </details>
            ))}
          </div>
        </motion.div>

        {/* Footer */}
        <div className="text-center py-6 space-y-2">
          <p className={`text-sm ${isDark ? 'text-[#6B6560]' : 'text-[#6B6560]'}`}>
            {t.madeWith}
          </p>
          <p className={`text-xs ${isDark ? 'text-[#6B6560]' : 'text-[#6B6560]'}`}>
            {t.version}
          </p>
          <div className="flex justify-center gap-1 pt-2">
            {[...Array(5)].map((_, i) => (
              <Star key={i} className="w-4 h-4 text-[#C84B32] fill-[#F5B942]" />
            ))}
          </div>
        </div>
      </div>
      <ShubhBottomNav />
    </div>
  );
};

export default HelpPage;
